import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { ApplicationService } from '../../../services/application/application.service';
import { SubjectManager } from '../../../../../common/js/subjectManager/SubjectManager';
import { Component, OnInit } from '@angular/core';
import { CoverpathControllerInterface } from '../../../interfaces/controller/CoverpathControllerInterface';
import { Router } from '@angular/router';
import { Utils } from '../../../../../common/js/Utils';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { Subscription } from 'rxjs';
import { GeoChangeService } from '../../../services/geoChange/geoChange.service';
import { Product } from '../../../../../common/js/static/Product';
import { Assumptions } from '../../../../../common/js/static/Assumptions';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

@Component({
  moduleId: module.id,
  selector: 'policy-details',
  templateUrl: './policy-details.component.html',
})
export class PolicyDetailsComponent implements OnInit {
  filingNumber: string;
  isAdvisor: boolean;
  isTeleInterviewerLoggedIn: boolean;
  controller: CoverpathControllerInterface;
  applicantName: string = '';
  insuredName: string = '';
  formattedCoverageAmount: string = '';
  lengthType: string = '';
  productType: string = '';
  showEdit: boolean = true;
  isJointApp: boolean = false;
  advisorName: string = '';
  licenseNumber: string;
  shouldShowFlAdvisorDetails: boolean = false;
  homeOfficeAddress: string = '';
  adminOfficeAddress: string = '';

  private fillingNumberChangeSubscription: Subscription;

  constructor(
    private amorphicService: AmorphicService,
    private applicationService: ApplicationService,
    private geoChangeService: GeoChangeService,
    private router: Router
  ) {
    this.controller = this.amorphicService.controller;
    this.isAdvisor = applicationService.appState.isAdvisor;
    this.isTeleInterviewerLoggedIn = applicationService.appState.isTeleInterviewerLoggedIn;
  }

  private async setProperties() {
    this.isJointApp = this.controller.customer.application.isJointApp();
    this.formattedCoverageAmount = Utils.formatIntCurrency(
      this.controller.customer.application.getTotalFaceAmount()?.toString()
    );
    const primaryPolicy = this.controller.customer.application.primaryPolicy;
    this.filingNumber = CoverpathUtils.getFilingNumber(primaryPolicy);
    const ownerName: string =
      primaryPolicy.owner.amorphicGetClassName() === 'Person'
        ? primaryPolicy.ownerPerson.firstNameLastName
        : primaryPolicy.ownerEntity.getFullNameOfOwner();
    this.insuredName = primaryPolicy.insured.person.firstNameLastName;
    const customerName: string = this.controller.customer.firstNameLastName;
    this.applicantName = CoverpathUtils.getOwnerNameForUIDisplay(ownerName, this.insuredName, customerName);
    const isFloridaApplication: boolean = primaryPolicy.getApplicationState() === 'FL';

    if (isFloridaApplication && !this.isTeleInterviewerLoggedIn) {
      this.shouldShowFlAdvisorDetails = true;
      if (primaryPolicy.isCoverpathPolicy()) {
        this.advisorName = primaryPolicy.primaryPolicyAgent.agent.person.fullName;
        this.licenseNumber = await this.getFloridaLicense(primaryPolicy.primaryPolicyAgent.agent.producerId);
      } else if (primaryPolicy.isMMDirectPolicy()) {
        this.advisorName = Assumptions.MMD_FL_InsuranceProducer;
        this.licenseNumber = Assumptions.MMD_FL_LicenseNumber;
      }
    }

    this.showEdit = !this.isJointApp && primaryPolicy.isTermLifePolicy();
    ({ lengthText: this.lengthType, productText: this.productType } = CoverpathUtils.getAppTaglines(
      this.controller.customer.application
    ));
    const channel = Product.channels[primaryPolicy.getChannelCode()];
    if (channel) {
      this.homeOfficeAddress = channel.homeOfficeAddress;
      this.adminOfficeAddress = channel.adminOfficeAddress;
    }
  }

  async ngOnInit() {
    await this.setProperties();
    this.fillingNumberChangeSubscription = this.geoChangeService.stateChangeSource$.subscribe(() => {
      const primaryPolicy = this.controller.customer.application.primaryPolicy;
      this.filingNumber = CoverpathUtils.getFilingNumber(primaryPolicy);
    });
    const updateFaceTermSubject = SubjectManager.getSubject('update_faceterm');
    updateFaceTermSubject.subscribe(() => {
      this.setProperties();
    });
    SubjectManager.setSubject('update_faceterm', updateFaceTermSubject);

    const updateInsuredNameSubject = SubjectManager.getSubject('update_insuredName');
    updateInsuredNameSubject.subscribe(() => {
      const primaryPolicy = this.controller.customer.application.primaryPolicy;
      this.insuredName = primaryPolicy.insured.person.firstNameLastName;
    });
    SubjectManager.setSubject('update_insuredName', updateInsuredNameSubject);
  }

  ngOnDestroy(): void {
    if (this.fillingNumberChangeSubscription) {
      this.fillingNumberChangeSubscription.unsubscribe();
    }
  }

  private async getFloridaLicense(producerId: string): Promise<string | null> {
    try {
      const agentLicenseList = await this.controller.getStateLicenses(producerId);
      return CoverpathUtils.getFloridaLicense(agentLicenseList, producerId);
    } catch (err) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'PolicyDetailsComponent',
          category: UICategory.request,
          function: 'getFloridaLicense',
          error: err,
        },
        `No FL license found for producerId: ${producerId}`
      );
      return Promise.resolve(null);
    }
  }
}
